/**
 * Single Kubernetes event row — shared between {@link EventsPanel}
 * (cluster-wide events list) and {@link WorkloadDrawer} (workload-scoped
 * events tab, #243).
 *
 * Extracted so the two callers stay visually consistent — the per-event
 * markup + the `timeAgo` helper bundle together cleanly. Keep this file
 * dumb (no data fetching, no role gates); the list-level concerns (filter
 * bar, empty state) live in the callers.
 */
import { AlertTriangle, Info } from 'lucide-react';
import type { ClusterEvent } from '@jeket-cloud/types';

function timeAgo(timestamp: string): string {
  if (!timestamp) return '';
  const ms = Date.now() - new Date(timestamp).getTime();
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

interface Props {
  event: ClusterEvent;
}

export default function EventRow({ event }: Props) {
  return (
    <div
      data-testid="cluster-event-row"
      className={`px-4 py-3 hover:bg-surface-elevated/30 transition-colors ${
        event.type === 'Warning' ? 'border-l-2 border-l-status-warning' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        {event.type === 'Warning' ? (
          <AlertTriangle size={14} className="text-status-warning mt-0.5 shrink-0" />
        ) : (
          <Info size={14} className="text-status-info mt-0.5 shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-silver-bright">{event.reason}</span>
            <span className="text-xs text-silver-muted font-mono">
              {event.involvedObject.kind}/{event.involvedObject.name}
            </span>
            <span className="text-xs text-silver-muted">{event.namespace}</span>
            {event.count > 1 && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-surface-elevated text-silver-muted">
                x{event.count}
              </span>
            )}
            <span className="text-xs text-silver-muted ml-auto">{timeAgo(event.lastTimestamp)}</span>
          </div>
          <p className="text-xs text-silver-muted mt-0.5 truncate">{event.message}</p>
        </div>
      </div>
    </div>
  );
}
